import React from "react";
import _ from "lodash";
import clsx from "clsx";
import { connect } from 'react-redux';

import {isWidthDown, withWidth} from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Skeleton from "@material-ui/lab/Skeleton";

import PrimaryButton from "../../components/Buttons/PrimaryButton";
import Link from "../../components/Link";
import {openInNewTab} from "../../utils/utils";
import walnutImg from "./images/walnut.jpg";
import icfImg from "./images/icf.jpg";
import ServiceIcon from './images/service';
import Carousel from "../../components/Carousel";
import {BadgeForNew} from "../../components/Badge";
import {BrokerResource} from "../../utils/api";
import {displayErrorSnackBar} from "../../components/SnackbarProvider/actions";
import {getOriginLink, SERVER_ERROR} from "../../utils/constants";
import BaseDialog from "../../components/BaseDialog/BaseDialog";
import SelectFormElementV2 from "../RiskProfiling/components/StepContent/components/formElement/SelectFormElementV2";
import {PRODUCT_CATEGORY_ICONS, WALNUT_LOGIN_OPTIONS, CONTACT_1_INFO, CONTACT_2_INFO} from "./constants";
import {getAuthSelector} from "../../utils/redaxSelectors";
import GoogleAnalytics from "../../utils/GoogleAnalytics";

import styles from "./styles";

const NewsSection = withStyles(styles)(({classes, news, loading}) => {

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Grid container>
          <Grid item xs className={classes.cardHeader}>News</Grid>
          <Grid item xs={'auto'}>
            <PrimaryButton
              variant={'text'}
              text={'Archiv'}
              iconPosition={'right'}
              icon={<i className={'fal fa-arrow-right'} />}
              onButtonClick={() => openInNewTab('https://www.bca.de/aif-news-archived/')}
              customClasses={{root: clsx(classes.blueBtn, classes.btnSmall)}}
            />
          </Grid>
        </Grid>
      </Grid>
			<Grid item xs={12}>
				<Carousel>
					{news.map(newsItem => (
						<Grid key={newsItem.id} container spacing={3}>
							<Grid item xs={12}>
								<div className={classes.newsImage}>
									{loading ? <Skeleton variant={'rect'} height={'100%'} /> : <img alt={''} src={_.get(newsItem, 'image.data.attributes.url')} />}
								</div>
							</Grid>
							<Grid item xs={12}>
								<Grid container spacing={1}>
									<Grid item xs={12} className={classes.newsHeadline}>
										{loading ? <Skeleton /> : newsItem.title}
									</Grid>
									<Grid item xs={12} className={classes.newsDescription}>
										{loading ? <><Skeleton /><Skeleton /></> : newsItem.description}
									</Grid>
									<Grid item xs={12}>
										{loading ? <Skeleton width={100}/> :
											<Link
												underline
												title={'Mehr erfahren'}
												onClick={() => openInNewTab(newsItem.url)}
												customClasses={{link: classes.newsDetailsBtn}}
											/>
										}
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					))}
				</Carousel>
			</Grid>
    </Grid>
  )
});

const ProductsSection = withWidth()(withStyles(styles)(({classes, products, loading, width}) => {
	const [currentIndex, setCurrentIndex] = React.useState(0);
 	const [paused, setPaused] = React.useState(false);
 	const timeoutRef = React.useRef(null);
 	const totalSlides = (products || []).length;

 	const topLinkClick = React.useCallback(() => openInNewTab(getOriginLink('/#/Infothek/Geschlossene%20AIF/8')), []);

	React.useEffect(() => {
    if (loading || paused || totalSlides <= 1) return;

    timeoutRef.current = setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % totalSlides);
    }, 5000);

    return () => clearTimeout(timeoutRef.current);
  }, [currentIndex, paused, loading]);

	const onProductClick = (idx) => () => {
		setCurrentIndex(idx);
	};

	const selectedProduct = _.get(products, currentIndex);
	const isMobile = isWidthDown('sm', width, true);
	const isActive = (p) => _.get(p, 'data.id') && _.get(p, 'data.id') === _.get(selectedProduct, 'data.id');

  return (
    <Grid container spacing={3} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <Grid item xs={12} md={7}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container>
              <Grid item xs className={classes.cardHeader}>
                Top-Produkte im Überblick
              </Grid>
              <Grid item xs={'auto'} style={{position: 'relative'}}>
                <IconButton
                  onClick={topLinkClick}
                  classes={{root: clsx(classes.bigIcon, classes.btnArrow, classes.blueBtn)}}
                ><i className={'fal fa-arrow-right'} /></IconButton>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container>
              {products.map((p, idx) => ((!isMobile || isActive(p)) && (
                <Grid key={p.id} item xs={12} className={clsx(classes.productItem, isActive(p) && 'active')} onClick={onProductClick(idx)}>
                  <Grid container spacing={1} alignItems={"center"}>
                    <Grid item xs={'auto'}>
                      <div className={classes.productIcon}>
                        {loading ? <Skeleton variant={'circle'} height={'100%'} /> : <i className={`fal ${PRODUCT_CATEGORY_ICONS[_.get(p, 'data.attributes.icon')]}`} />}
                      </div>
                    </Grid>
                    <Grid item xs>
                      <div className={classes.productTitle}>
												{loading ? <Skeleton /> : (
													<>{_.get(p, 'data.attributes.title')}{_.get(p, 'data.attributes.new') && <BadgeForNew />}</>
												)}
                      </div>
                      <div className={classes.productDescription}>{loading ? <><Skeleton /><Skeleton /></> : _.get(p, 'data.attributes.description')}</div>
                    </Grid>
                  </Grid>
                </Grid>
              )))}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={5}>
        <div className={classes.productImage}>
          {loading ? <Skeleton variant={"rect"} height={'100%'} /> : (
            selectedProduct && <img alt='' src={_.get(selectedProduct, 'data.attributes.image.data.attributes.url')} />
          )}
        </div>
      </Grid>
    </Grid>
  )
}));

const NewsAndProducts = connect()(withStyles(styles)(({classes, dispatch}) => {
  const [data, setData] = React.useState({
    data: undefined,
    loading: true
  });

  React.useEffect(() => {
    BrokerResource.at('aif-landing-page/').get().then(data => {
      const res = _.get(data, 'data.attributes', {});

    	setData({
			  data: {
			    news: res.news || [],
			    products: [res.topProduct1, res.topProduct2, res.topProduct3, res.topProduct4].filter(p => !!p)
			  },
			  loading: false
			})
		}).catch((e) => {
			console.error(e);
			dispatch(displayErrorSnackBar(SERVER_ERROR));

			setData({
			  data: {
			    news: [],
			    products: []
			  },
			  loading: false
			})
		});
  }, []);

  return (
    <Grid item xs={12}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <div className={classes.card}>
            <NewsSection
              loading={data.loading}
              news={data.loading ? [{}] : data.data.news}
            />
          </div>
        </Grid>
        <Grid item xs={12} md={8}>
          <div className={classes.card}>
            <ProductsSection
              loading={data.loading}
              products={data.loading ? [{}, {}, {}, {}] : data.data.products}
            />
          </div>
        </Grid>
      </Grid>
    </Grid>
  )
}));

const WalnutCard = ({classes, broker_id}) => {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [contactModalOpen, setContactModalOpen] = React.useState(false);
  const [selectedLoginURL, setSelectedLoginURL] = React.useState(undefined);

  const closeModal = () => setModalOpen(false);
  const closeContactModal = () => setContactModalOpen(false);
  const onClick = () => {
    setModalOpen(true);

    GoogleAnalytics.trackAIFTileClick('Walnut', broker_id);
  };

  return (
    <>
      <div className={clsx(classes.card, classes.cardShadow)} onClick={onClick}>
        <Grid container>
          <Grid item xs className={classes.cardHeader}>
            <img alt={''} src={walnutImg} className={classes.cardLogo} />
          </Grid>
          <Grid item xs={'auto'}>
            <i className={clsx('fal fa-arrow-right', classes.bigIcon, classes.btnGrey)} />
          </Grid>
        </Grid>
        <div className={'blue-card'}>
          <Grid container spacing={1}>
          <Grid item xs={12} className={classes.cardHeader}>Walnut</Grid>
          <Grid item xs={12}>WALNUT LIVE: Ihre digitale Dokumentation- und Zeichnungs-Strecke! Eine Lösung für drei Anwendungsbereiche:</Grid>
          <Grid item xs={12}>
            <Grid container spacing={1}>
              <Grid item xs={'auto'}><i className={'fal fa-check'} style={{color: '#4CAF50'}} /></Grid>
              <Grid item xs>Zeichnungsunterlagen digital vorbereiten und ausdrucken.</Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid item xs={'auto'}><i className={'fal fa-check'} style={{color: '#4CAF50'}} /></Grid>
              <Grid item xs>Digitale Zeichnung und Dokumentation vor Ort mit dem Kunden zusammen.</Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid item xs={'auto'}><i className={'fal fa-check'} style={{color: '#4CAF50'}} /></Grid>
              <Grid item xs>Online-Webmeeting mit dem Kunden zur digitalen Erstellung aller Unterlagen mit Vollständigkeitsprüfung samt Aufzeichnung.</Grid>
            </Grid>
          </Grid>
        </Grid>
        </div>
      </div>
      <BaseDialog
        title={'Wichtige Info bevor es mit Walnut weitergeht'}
        open={modalOpen}
        handleClose={closeModal}
        maxWidth={'sm'}
        scroll={'paper'}
        classes={{paper: classes.dialogPaper}}
        customClasses={{dialogTitle: classes.dialogTitle}}
        actions={
          <Grid container spacing={2}>
            <Grid item xs={12} sm={'auto'}>
              <PrimaryButton
                color={'secondary'}
                variant={'outlined'}
                text={'Abbrechen'}
                onButtonClick={closeModal}
                style={{width: '100%'}}
              />
            </Grid>
            <Grid item xs={12} sm={'auto'} style={{marginLeft: 'auto'}}>
              <PrimaryButton
                variant={'outlined'}
                text={'Kontakt'}
                customClasses={{root: classes.contactDialogBtn}}
                style={{width: '100%'}}
                onButtonClick={() => {
                  closeModal();
                  setContactModalOpen(true)
                }}
              />
            </Grid>
            <Grid item xs={12} sm={'auto'}>
              <PrimaryButton
                text={'Weiter zu Walnut'}
                customClasses={{root: classes.dialogSuccessBtn}}
                disabled={!selectedLoginURL}
                onButtonClick={() => openInNewTab(selectedLoginURL)}
                style={{width: '100%'}}
              />
            </Grid>
          </Grid>
        }
      >
        <div className={classes.dialogContentBlock}></div>
        <div className={classes.dialogContentBlock}>
          Sie werden im Anschluss zu Walnut weitergeleitet. Bitte halten Sie Ihre Login-Daten bereit. Wählen Sie
          bitte zunächst aus, ob Sie BCA-, BfV- oder CARAT-Partner sind. Falls Sie noch keine Zugangsdaten haben,
          wenden Sie sich bitte an die Sachwertexperten zu Walnut.
        </div>
        <div className={classes.dialogContentBlock}>
          <SelectFormElementV2
            label={'Bitte wählen Sie Ihren Walnut Login.'}
            value={selectedLoginURL}
            options={WALNUT_LOGIN_OPTIONS}
            onChange={setSelectedLoginURL}
            newDesign
          />
        </div>
        <div className={classes.dialogContentBlock}></div>
      </BaseDialog>
      <BaseDialog
        title={'Sachwertexpertin zu Walnut'}
        open={contactModalOpen}
        handleClose={closeContactModal}
        maxWidth={'sm'}
        scroll={'paper'}
        classes={{paper: classes.dialogPaper}}
        customClasses={{dialogTitle: classes.dialogTitle}}
        actions={
          <Grid container spacing={2}>
            <Grid item xs={12} sm={'auto'}>
              <PrimaryButton
                color={'secondary'}
                variant={'outlined'}
                text={'Abbrechen'}
                onButtonClick={closeContactModal}
                style={{width: '100%'}}
              />
            </Grid>
            <Grid item xs={12} sm={'auto'} style={{marginLeft: 'auto'}}>
              <PrimaryButton
                variant={'outlined'}
                text={'Zurück'}
                customClasses={{root: classes.contactDialogBtn}}
                style={{width: '100%'}}
                onButtonClick={() => {
                  closeContactModal();
                  setModalOpen(true)
                }}
              />
            </Grid>
          </Grid>
        }
      >
        <div className={classes.dialogContentBlock}></div>
        <div className={classes.dialogContentBlock}>
          <ContactCard classes={classes} contactInfo={CONTACT_2_INFO} />
        </div>
        <div className={classes.dialogContentBlock}></div>
      </BaseDialog>
    </>
  )
};

const ContactCard = ({classes, contactInfo}) => {
  const email = _.isFunction(contactInfo.getEmail) ? contactInfo.getEmail() : contactInfo.email;

  return (
    <Grid container spacing={2} alignItems={"center"}>
      <Grid item xs={'auto'}>
        <div className={classes.contactIcon}><img src={contactInfo.image} alt={''} /></div>
      </Grid>
      <Grid item xs>
        <div className={classes.cardHeader}>{contactInfo.name}</div>
        <div className={classes.contactRole}>{contactInfo.role}</div>
        <div>
          <PrimaryButton
            variant={'text'}
            text={email}
            icon={<i className={'fal fa-envelope'} />}
            customClasses={{label: classes.contactLink}}
            onButtonClick={() => openInNewTab(`mailto: ${email}`)}
          />
        </div>
        <Grid container spacing={1}>
          <Grid item xs>
            <PrimaryButton
              variant={'text'}
              text={contactInfo.phone}
              icon={<i className={'fal fa-phone'} />}
              customClasses={{label: classes.contactLink}}
              onButtonClick={() => openInNewTab(`tel: ${contactInfo.phone}`)}
            />
          </Grid>
          <Grid item xs>
            <PrimaryButton
              variant={'text'}
              text={contactInfo.mobile}
              icon={<i className={'fal fa-mobile-phone'} />}
              customClasses={{label: classes.contactLink}}
              onButtonClick={() => openInNewTab(`tel: ${contactInfo.mobile}`)}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

const mapStateToProps = (state) => ({
  auth: getAuthSelector(state)
});

const AIFLandingPage = connect(mapStateToProps)(({classes, auth}) => {
  const brokerId = auth.user.broker_id;

  const onICClick = () => {
    GoogleAnalytics.trackAIFTileClick('IC Finanzportal', brokerId);
    openInNewTab('https://www.ic-rosenheim.de/');
  };

  return (
    <Grid className={classes.container}>
      <Typography variant="h1" className={classes.header}>
        Sachwerte / Geschlossene AIF
      </Typography>
      <Grid container spacing={3}>
        <NewsAndProducts />
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md>
              <WalnutCard classes={classes} broker_id={brokerId} />
            </Grid>
            <Grid item xs={12} sm={6} md>
              <div className={clsx(classes.card, classes.cardShadow)} onClick={onICClick}>
                <Grid container>
                  <Grid item xs className={classes.cardHeader}>
                    <img alt={''} src={icfImg} className={classes.cardLogo} />
                  </Grid>
                  <Grid item xs={'auto'}>
                    <i className={clsx('fal fa-arrow-right', classes.bigIcon, classes.btnGrey)} />
                  </Grid>
                </Grid>
                <div className={'blue-card'}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} className={classes.cardHeader}>IC Finanzportal</Grid>
                    <Grid item xs={12}>IC CONSULTING: Ihr persönlicher Sachwertdienstleister!</Grid>
                    <Grid item xs={12}>
                      <Grid container spacing={1}>
                        <Grid item xs={'auto'}><i className={'fal fa-check'} style={{color: '#4CAF50'}} /></Grid>
                        <Grid item xs>Unterstützt Sie bei der Auswahl geeigneter und angemessener Produkte.</Grid>
                      </Grid>
                      <Grid container spacing={1}>
                        <Grid item xs={'auto'}><i className={'fal fa-check'} style={{color: '#4CAF50'}} /></Grid>
                        <Grid item xs>Bereitet mit Ihnen Kundengespräche vor.</Grid>
                      </Grid>
                      <Grid container spacing={1}>
                        <Grid item xs={'auto'}><i className={'fal fa-check'} style={{color: '#4CAF50'}} /></Grid>
                        <Grid item xs>Bespricht mit Ihnen die optimale Portfolioaufstellung.</Grid>
                      </Grid>
                      <Grid container spacing={1}>
                        <Grid item xs={'auto'}><i className={'fal fa-check'} style={{color: '#4CAF50'}} /></Grid>
                        <Grid item xs>Gestaltet mit Ihnen Werbemaßnahmen wie Kundenanschreiben.</Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={'auto'}>
              <Grid container spacing={2} className={classes.filesContainer}>
                <Grid item xs={12} sm={6} md={12}>
                  <Grid className={clsx(classes.card, classes.cardShadow, classes.blueBtn)} onClick={() => openInNewTab(getOriginLink('/#/Infothek/Geschlossene%20AIF/8'))}>
                    <Grid container spacing={1} alignItems={'center'} alignContent={"center"} style={{textAlign: "center", height: '100%'}}>
                      <Grid item xs={12}><i className={clsx(classes.bigIcon, 'fal fa-file')} /></Grid>
                      <Grid item xs={12} className={classes.fileLink}>Präsentations- und Informationsunterlagen</Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={6} md={12}>
                  <Grid className={clsx(classes.card, classes.cardShadow, classes.blueBtn)} onClick={() => openInNewTab(getOriginLink('/#/Infothek/Courtagelisten/742/840'))}>
                    <Grid container spacing={1} alignItems={'center'} alignContent={"center"} style={{textAlign: "center", height: '100%'}}>
                      <Grid item xs={12}><i className={clsx(classes.bigIcon, 'fal fa-file')} /></Grid>
                      <Grid item xs={12} className={classes.fileLink}>Produkt- und Provisionslisten</Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <div className={classes.card} style={{paddingBottom: 32}}>
                <Grid container spacing={2}>
                  <Grid item xs={12} className={classes.cardHeader}>Ihr Sachwertexperten Service</Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={3} alignItems={"center"}>
                      <Grid item xs={12} sm={4} className={classes.serviceImage}><ServiceIcon /></Grid>
                      <Grid item xs={12} sm={8}>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <Grid container spacing={2}>
                              <Grid item xs={'auto'}>
                                <i className={clsx(classes.serviceCheckIcon, 'fal fa-check-circle')} />
                              </Grid>
                              <Grid item xs>
                                <div className={classes.servicePointHeader}>
                                  Auswahl & Prüfung von wirtschaftlich plausiblen AIFs
                                </div>
                                <div className={classes.servicePointDescription}>
                                  Wirtschaftlich plausible AIFs mit verbessertem Rendite-Risiko-Profil und optimiertem Vertragswerk
                                </div>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Grid container spacing={2}>
                              <Grid item xs={'auto'}>
                                <i className={clsx(classes.serviceCheckIcon, 'fal fa-check-circle')} />
                              </Grid>
                              <Grid item xs>
                                <div className={classes.servicePointHeader}>
                                  Übernahme von Pflichten
                                </div>
                                <div className={classes.servicePointDescription}>
                                  Erfüllung von FinVermV/WpIG-Vorschriften, inklusive digitaler, rechtssicherer Beratungs- und Zeichnungsstrecke
                                </div>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Grid container spacing={2}>
                              <Grid item xs={'auto'}>
                                <i className={clsx(classes.serviceCheckIcon, 'fal fa-check-circle')} />
                              </Grid>
                              <Grid item xs>
                                <div className={classes.servicePointHeader}>
                                  Pre-Sales-Service
                                </div>
                                <div className={classes.servicePointDescription}>
                                  Individuelle Unterstützung bei Produktauswahl und Gesprächsvorbereitung für Anleger
                                </div>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Grid container spacing={2}>
                              <Grid item xs={'auto'}>
                                <i className={clsx(classes.serviceCheckIcon, 'fal fa-check-circle')} />
                              </Grid>
                              <Grid item xs>
                                <div className={classes.servicePointHeader}>
                                  Unabhängig
                                </div>
                                <div className={classes.servicePointDescription}>
                                  Sie wählen Ihr Portfolio aus unserer Matrix – mit vollständigen Informationen
                                </div>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Grid container spacing={2}>
                              <Grid item xs={'auto'}>
                                <i className={clsx(classes.serviceCheckIcon, 'fal fa-check-circle')} />
                              </Grid>
                              <Grid item xs>
                                <div className={classes.servicePointHeader}>
                                  After-Sales-Service
                                </div>
                                <div className={classes.servicePointDescription}>
                                  Unabhängiger Ansprechpartner und Berater, auch wenn Anbieter nicht mehr unterstützen
                                </div>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Grid container spacing={2}>
                              <Grid item xs={'auto'}>
                                <i className={clsx(classes.serviceCheckIcon, 'fal fa-check-circle')} />
                              </Grid>
                              <Grid item xs>
                                <div className={classes.servicePointHeader}>
                                  Optionale Enthaftung
                                </div>
                                <div className={classes.servicePointDescription}>
                                  5-Punkte-Paket für Plausibilitätsprüfung, Aufsichtspflichten und Beratungsleistung
                                </div>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </div>
            </Grid>
            <Grid item xs={12} lg={4}>
              <div className={classes.card}>
                <Grid container spacing={2}>
                  <Grid item xs={12} style={{textAlign: 'center'}}>
                    <div className={classes.contactHeadline}>Sie haben Fragen oder ein Anliegen</div>
                    <div className={classes.cardHeader}>Ihre Sachwertexperten bei BCA:</div>
                  </Grid>
                  <Grid item xs={12}>{/* empty line for spacing  */}</Grid>
                  <Grid item xs={12}>
                    <ContactCard classes={classes} contactInfo={CONTACT_1_INFO} />
                  </Grid>
                  <Grid item xs={12}><div className={classes.contactsDivider}></div></Grid>
                  <Grid item xs={12}>
                    <ContactCard classes={classes} contactInfo={CONTACT_2_INFO} />
                  </Grid>
                </Grid>
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
});

export default withStyles(styles)(AIFLandingPage);